import React from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames/bind';
import * as d3 from 'd3';

import styles from './Donut.module.css';

const key = (x) => x.label;
const cx = classnames.bind(styles);

/**
 * **Access:**
 * ```js
 * import {DonutChart} from '@iss/client-react-components'
 * ```
 * Provides a donut chart.
 *
 * @class DonutChart
 * @component
 */
export default class DonutChart extends React.Component {
  static propTypes = {
    centerLabel: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    centerValue: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    centerTspanSize: PropTypes.string,
    data: PropTypes.arrayOf(
      PropTypes.shape({
        label: PropTypes.string.isRequired,
        value: PropTypes.number.isRequired,
      }).isRequired
    ).isRequired,
    significant: PropTypes.number,
    size: PropTypes.number,
    donutHoleSize: PropTypes.number,
    startAngle: PropTypes.number,
  };

  ref = React.createRef();

  get svg() {
    return d3.select(this.ref.current);
  }

  get graph() {
    const {
      svg,
      rect: { width, height },
      props: { centerValue, centerLabel },
    } = this;

    let graph = svg.select('g');

    if (graph.empty()) {
      graph = svg.append('g').attr('transform', 'translate(' + width / 2 + ',' + height / 2 + ')');
      graph.append('g').attr('class', 'slices');
      (centerValue || centerLabel) && graph.append('g').attr('class', 'center');
    }

    return graph;
  }

  get rect() {
    return this.ref.current.getBoundingClientRect();
  }

  get radius() {
    const {
      rect: { width, height },
      props: { size },
    } = this;

    return (Math.min(width, height) / 2) * (size || 1) * 0.65;
  }

  componentDidMount() {
    this.draw();
  }

  componentDidUpdate() {
    this.draw();
  }

  draw() {
    const {
      radius,
      props: { significant = 0.0, data = [], startAngle = 0, centerValue, centerLabel, donutHoleSize = 1 },
    } = this;

    const sum = data.reduce((a, x) => a + x.value, 0);

    this.pie = d3
      .pie()
      .sort(null)
      .startAngle(startAngle)
      .endAngle(startAngle + Math.PI * 2)
      .value((d) => d.value)(data.filter((x) => x.value / sum > significant));

    const donutHole = donutHoleSize * 8;

    const arc = d3
      .arc()
      .outerRadius(radius)
      .innerRadius(radius - donutHole)
      .padAngle(1.0 / radius);

    this.drawPieSlices(arc);

    this.drawCenterValues(centerValue, centerLabel);
  }

  drawPieSlices(arc) {
    const slice = this.graph.select('.slices').selectAll('path.slice').data(this.pie, key);

    slice
      .enter()
      .insert('path')
      .style('fill', (d) => d.data.color)
      .attr('class', 'slice')
      .attr('d', (t) => arc(t))
      .attr('data-index', (d) => d.index);
  }

  drawCenterValues(centerValue, centerLabel) {
    const { centerTextColor = '#000000', centerTextFontSize = '.15in', centerTspanSize = '-13%' } = this.props;
    let graphTag = this.graph.select('.center').selectAll('text').data(this.pie, key).enter();
    if (centerValue) {
      graphTag = graphTag
        .append('text')
        .attr('text-anchor', 'middle')
        .attr('font-size', centerTextFontSize)
        .attr('fill', centerTextColor)
        .attr('dy', centerLabel ? '8%' : '0')
        .text(centerValue);
    }
    if (centerLabel) {
      graphTag
        .append('tspan')
        .attr('x', '0')
        .attr('dy', centerTspanSize)
        .attr('font-size', centerTextFontSize)
        .attr('fill', centerTextColor)
        .text(centerLabel);
    }
  }

  render() {
    const { className, title } = this.props;
    return (
      <div className={cx('root', className)}>
        {title && <h4>{title}</h4>}
        <svg ref={this.ref} className={cx('graph')} />
      </div>
    );
  }
}
